import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TestSignalModal } from '@/components/workflows/TestSignalModal';
import { ModeToggle } from '@/components/mode-toggle';
import { MobileMenuButton } from '@/components/MobileMenuButton';
import {
  Loader2, Play, Pause, Edit, Clock, Zap, FlaskConical,
  CheckCircle2, XCircle, AlertCircle, Timer, Ban,
  ChevronLeft, ChevronRight, RotateCcw, GitBranch,
} from 'lucide-react';
import {
  ReactFlow,
  Background,
  MarkerType,
  BackgroundVariant,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { TriggerNode } from '../components/workflows/nodes/TriggerNode';
import { ActionNode } from '../components/workflows/nodes/ActionNode';
import { ConditionNode } from '../components/workflows/nodes/ConditionNode';
import { ApprovalNode } from '../components/workflows/nodes/ApprovalNode';
import { LoopNode } from '../components/workflows/nodes/LoopNode';
import { ForEachNode } from '../components/workflows/nodes/ForEachNode';
import { WorkflowEdge } from '../components/workflows/WorkflowEdge';
import { UserAvatar } from '../components/UserAvatar';
import { useAuth } from '../hooks/useAuth';
import { useMobile } from '../hooks/useMobile';
import {
  apiClient,
  type WorkflowSummary,
  type WorkflowExecution,
  type CustomBlock,
} from '../api/client';

const previewNodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  approval: ApprovalNode,
  refine_approval: ApprovalNode,
  loop: LoopNode,
  for_each: ForEachNode,
};
const previewEdgeTypes = { workflow: WorkflowEdge };

const STATUS_CONFIG: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  draft:    { variant: 'secondary', label: 'Draft' },
  active:   { variant: 'default', label: 'Active' },
  paused:   { variant: 'outline', label: 'Paused' },
  archived: { variant: 'destructive', label: 'Archived' },
};

const EXEC_STATUS: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  running:          { icon: <Loader2 className="w-4 h-4 animate-spin" />, color: 'text-blue-600', label: 'Running' },
  completed:        { icon: <CheckCircle2 className="w-4 h-4" />, color: 'text-green-600', label: 'Completed' },
  failed:           { icon: <XCircle className="w-4 h-4" />, color: 'text-red-600', label: 'Failed' },
  cancelled:        { icon: <Ban className="w-4 h-4" />, color: 'text-muted-foreground', label: 'Cancelled' },
  waiting_approval: { icon: <Timer className="w-4 h-4" />, color: 'text-amber-600', label: 'Awaiting Approval' },
  waiting:          { icon: <Timer className="w-4 h-4" />, color: 'text-blue-600', label: 'Waiting' },
};

const WorkflowDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const PAGE_SIZE = 10;
  const [workflow, setWorkflow] = useState<WorkflowSummary | null>(null);
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [totalExecutions, setTotalExecutions] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [customBlocks, setCustomBlocks] = useState<CustomBlock[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'executions' | 'versions'>('executions');
  const [versions, setVersions] = useState<any[]>([]);
  const [totalVersions, setTotalVersions] = useState(0);
  const [versionPage, setVersionPage] = useState(1);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [userPicMap, setUserPicMap] = useState<Record<string, string>>({});
  const [rollbackTarget, setRollbackTarget] = useState<number | null>(null);

  const loadExecutions = useCallback(async (p: number) => {
    if (!id) return;
    try {
      const result = await apiClient.listWorkflowExecutions(id, p, PAGE_SIZE);
      setExecutions(result.executions || []);
      setTotalExecutions(result.total);
    } catch { /* silent */ }
  }, [id]);

  const loadVersions = useCallback(async (p: number = 1) => {
    if (!id) return;
    setVersionsLoading(true);
    try {
      const result = await apiClient.listWorkflowVersions(id, p, PAGE_SIZE);
      setVersions(result.versions || []);
      setTotalVersions(result.total);
    } catch { setVersions([]); }
    finally { setVersionsLoading(false); }
  }, [id]);

  const handleRollback = async (versionNumber: number) => {
    if (!id) return;
    try {
      await apiClient.rollbackWorkflow(id, versionNumber);
      setRollbackTarget(null);
      // Refresh data without full page reload, keep Deployments tab active
      const wf = await apiClient.getWorkflow(id);
      setWorkflow(wf);
      loadVersions(1);
    } catch (err: any) {
      setError(err?.message || 'Failed to rollback');
    }
  };

  useEffect(() => {
    if (!authLoading && user && id) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, id]);

  useEffect(() => {
    if (!user || !id) return;
    const interval = setInterval(async () => {
      try {
        const wf = await apiClient.getWorkflow(id);
        setWorkflow(wf);
      } catch { /* silent */ }
      loadExecutions(page);
    }, 10000);
    return () => clearInterval(interval);
  }, [user, id, page, loadExecutions]);

  useEffect(() => {
    if (activeTab === 'versions' && versions.length === 0) {
      loadVersions(versionPage);
    }
  }, [activeTab, versions.length, loadVersions, versionPage]);

  // Also load version count eagerly for the tab badge
  useEffect(() => {
    if (id && user) loadVersions(1);
  }, [id, user, loadVersions]);

  // Load user picture map for avatars
  useEffect(() => {
    apiClient.listTenantUsers().then((data: any) => {
      const users = (data as any).users || data || [];
      const map: Record<string, string> = {};
      for (const u of users) {
        if (u.email && u.picture_url) map[u.email] = u.picture_url;
      }
      setUserPicMap(map);
    }).catch(() => {});
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [wf, execResult] = await Promise.all([
        apiClient.getWorkflow(id!),
        apiClient.listWorkflowExecutions(id!, 1, PAGE_SIZE),
      ]);
      setWorkflow(wf);
      setExecutions(execResult.executions || []);
      setTotalExecutions(execResult.total);
      apiClient.listCustomBlocks().then(setCustomBlocks).catch(() => {});
    } catch (err: any) {
      setError(err?.message || 'Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadExecutions(newPage);
  };

  const totalPages = Math.ceil(totalExecutions / PAGE_SIZE);

  const handleActivate = async () => {
    if (!workflow) return;
    try {
      await apiClient.activateWorkflow(workflow.id);
      setWorkflow({ ...workflow, status: 'active' });
    } catch (err: any) {
      setError(err?.message || 'Failed to activate');
    }
  };

  const handlePause = async () => {
    if (!workflow) return;
    try {
      await apiClient.pauseWorkflow(workflow.id);
      setWorkflow({ ...workflow, status: 'paused' });
    } catch (err: any) {
      setError(err?.message || 'Failed to pause');
    }
  };

  const [testLoading, setTestLoading] = useState(false);
  const [showTestConfirm, setShowTestConfirm] = useState(false);
  const handleTestConfirmed = async (customSignal?: Record<string, any>) => {
    if (!workflow) return;
    setShowTestConfirm(false);
    setTestLoading(true);
    try {
      const exec = await apiClient.testWorkflow(workflow.id, customSignal);
      navigate(`/workflows/${workflow.id}/executions/${exec.id}`);
    } catch (err: any) {
      setError(err?.message || 'Failed to start test execution');
    } finally {
      setTestLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!workflow) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Workflow not found.</AlertDescription>
      </Alert>
    );
  }

  const badge = STATUS_CONFIG[workflow.status] || STATUS_CONFIG.draft;
  const nodeCount = workflow.definition?.nodes?.length || 0;

  return (
    <div className="space-y-6">
      {/* Page header — matches PageHeader layout */}
      <div className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-4 bg-background">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="min-w-0 flex-1 flex items-start gap-1">
              <MobileMenuButton />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                  <h1 className="text-2xl font-bold text-foreground">{workflow.name}</h1>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                  {workflow.simulated && (
                    <Badge
                      variant="outline"
                      className="border-amber-500/50 text-amber-600 dark:text-amber-400"
                      title="Simulated workflow: built with integrations simulated as connected. It can only be test-run and cannot be activated."
                    >
                      Simulated
                    </Badge>
                  )}
                </div>
                {workflow.description && (
                  <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{workflow.description}</p>
                )}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Zap className="w-4 h-4" /> {nodeCount} steps
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" /> Triggered {workflow.trigger_count} times
                  </span>
                  {workflow.last_triggered_at && (
                    <span>Last triggered: {new Date(workflow.last_triggered_at).toLocaleString(undefined, { timeZone: 'UTC' })} UTC</span>
                  )}
                  <span>Last updated: {new Date(workflow.updated_at).toLocaleString(undefined, { timeZone: 'UTC' })} UTC{workflow.updated_by ? ` by ${workflow.updated_by}` : ''}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:shrink-0">
              <Button variant="outline" size="sm" onClick={() => setShowTestConfirm(true)} disabled={testLoading} className="gap-1">
                {testLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FlaskConical className="w-3 h-3" />} Test
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/workflows/${workflow.id}/edit`)} className="gap-1">
                <Edit className="w-3 h-3" /> Edit
              </Button>
              {(workflow.status === 'draft' || workflow.status === 'paused') && !workflow.simulated && (
                <Button size="sm" onClick={handleActivate} className="gap-1">
                  <Play className="w-3 h-3" /> Activate
                </Button>
              )}
              {workflow.status === 'active' && (
                <Button variant="outline" size="sm" onClick={handlePause} className="gap-1">
                  <Pause className="w-3 h-3" /> Pause
                </Button>
              )}
              <ModeToggle />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Compact workflow graph preview */}
      {workflow.definition && workflow.definition.nodes?.length > 0 && (
        <WorkflowGraphPreview definition={workflow.definition} customBlocks={customBlocks} />
      )}

      {/* Tabs: Executions / Versions */}
      <div>
        <div className="flex items-center gap-1 border-b border-border mb-4">
          <button
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'executions' ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('executions')}
          >
            Executions
            {totalExecutions > 0 && <span className="ml-1.5 text-xs text-muted-foreground">{totalExecutions}</span>}
          </button>
          <button
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'versions' ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('versions')}
          >
            Deployments
            {totalVersions > 0 && <span className="ml-1.5 text-xs text-muted-foreground">{totalVersions}</span>}
          </button>
        </div>

        {activeTab === 'executions' && (
        <div>
        {executions.length === 0 && page === 1 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <p className="text-sm">No executions yet. Activate the workflow to start processing signals.</p>
            </CardContent>
          </Card>
        ) : (
          <>
          <div className="rounded-lg border overflow-x-auto md:overflow-hidden">
            <table className="w-full text-sm min-w-[640px] md:min-w-0">
              <thead>
                <tr className="border-b">
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-10"></th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Execution</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Started</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Duration</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Error</th>
                </tr>
              </thead>
              <tbody>
            {executions.map(exec => {
              const status = EXEC_STATUS[exec.status] || EXEC_STATUS.running;
              const duration = exec.completed_at
                ? formatDuration(new Date(exec.started_at), new Date(exec.completed_at))
                : formatDuration(new Date(exec.started_at), new Date());
              const signal = typeof exec.trigger_signal === 'string'
                ? (() => { try { return JSON.parse(exec.trigger_signal); } catch { return null; } })()
                : exec.trigger_signal;
              const isTest = signal?.test_execution === true || signal?.metadata?.test_execution === true;
              return (
                <tr
                  key={exec.id}
                  className="border-b last:border-0 hover:bg-muted/20 transition-colors cursor-pointer"
                  onClick={() => navigate(`/workflows/${workflow.id}/executions/${exec.id}`)}
                >
                  <td className="px-4 py-2.5">
                    <span className={status.color}>{status.icon}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <span className={`font-medium ${status.color}`}>{status.label}</span>
                      {isTest && <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/50 text-amber-600 dark:text-amber-400">Test</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground font-mono text-xs">{exec.id.slice(0, 8)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground text-xs">
                    {new Date(exec.started_at).toLocaleString(undefined, { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'short' })} UTC
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground text-xs">{duration}</td>
                  <td className="px-4 py-2.5 text-xs text-destructive max-w-xs truncate">
                    {exec.error_message || ''}
                  </td>
                </tr>
              );
            })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-muted-foreground">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalExecutions)} of {totalExecutions}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
        )}

        {activeTab === 'versions' && (
          <div>
            {versionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : versions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <p className="text-sm">No version history yet. Versions are created each time the workflow is saved.</p>
                </CardContent>
              </Card>
            ) : (
              <>
              <div className="border rounded-lg overflow-x-auto md:overflow-hidden">
                <table className="w-full text-sm min-w-[560px] md:min-w-0">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left px-4 py-2 font-medium">Version</th>
                      <th className="text-left px-4 py-2 font-medium">Created</th>
                      <th className="text-left px-4 py-2 font-medium">By</th>
                      <th className="text-left px-4 py-2 font-medium">Status</th>
                      <th className="text-right px-4 py-2 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.map((v, idx) => (
                      <tr
                        key={v.id}
                        className={`border-b last:border-0 hover:bg-muted/20 cursor-pointer ${idx === 0 ? 'border-l-4 border-l-green-500' : ''}`}
                        onClick={() => navigate(`/workflows/${id}/edit`, { state: { suggestedDefinition: { definition: v.definition, trigger_config: v.trigger_config, name: v.name, description: v.description } } })}
                      >
                        <td className="px-4 py-3">
                          <code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">v{v.version_number}</code>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {new Date(v.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <UserAvatar email={v.created_by_email || ''} pictureUrl={userPicMap[v.created_by_email || '']} />
                            <span className="text-xs text-muted-foreground truncate max-w-[140px]">{v.created_by_email || '—'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {v.change_summary && v.change_summary !== 'Initial creation' ? (
                            <span className="text-xs text-muted-foreground">{v.change_summary}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">Edited</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {idx !== 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
                              onClick={(e) => { e.stopPropagation(); setRollbackTarget(v.version_number); }}
                            >
                              <RotateCcw className="w-3 h-3" /> Rollback
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalVersions > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-muted-foreground">
                    Showing {(versionPage - 1) * PAGE_SIZE + 1}–{Math.min(versionPage * PAGE_SIZE, totalVersions)} of {totalVersions}
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={versionPage <= 1} onClick={() => { setVersionPage(versionPage - 1); loadVersions(versionPage - 1); }}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" disabled={versionPage * PAGE_SIZE >= totalVersions} onClick={() => { setVersionPage(versionPage + 1); loadVersions(versionPage + 1); }}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Rollback confirmation modal */}
      {rollbackTarget !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setRollbackTarget(null)}>
          <div className="bg-background border rounded-lg shadow-xl max-w-sm w-full m-4" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4">
              <h3 className="text-base font-semibold">Roll back to v{rollbackTarget}?</h3>
            </div>
            <div className="px-5 pb-4">
              <p className="text-sm text-muted-foreground">This will save the current state as a new version and then apply v{rollbackTarget}'s configuration.</p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3">
              <Button variant="outline" size="sm" onClick={() => setRollbackTarget(null)}>Cancel</Button>
              <Button size="sm" onClick={() => handleRollback(rollbackTarget)}>Rollback</Button>
            </div>
          </div>
        </div>
      )}

      <TestSignalModal
        workflowId={workflow?.id || ''}
        triggerConfig={workflow?.trigger_config ? (typeof workflow.trigger_config === 'string' ? JSON.parse(workflow.trigger_config) : workflow.trigger_config) : null}
        open={showTestConfirm}
        onClose={() => setShowTestConfirm(false)}
        onRunTest={handleTestConfirmed}
        loading={testLoading}
      />
    </div>
  );
};

function WorkflowGraphPreview({ definition, customBlocks }: { definition: { nodes: any[]; edges: any[] }; customBlocks?: CustomBlock[] }) {
  // On phones the preview container is narrow, so wide workflows need a zoom
  // below React Flow's default minZoom (0.5) for fitView to fit them; desktop
  // keeps the default so the web view is unchanged.
  const isMobile = useMobile();
  const MARKER_END = { type: MarkerType.ArrowClosed as MarkerType, width: 16, height: 16 };

  const nodes: Node[] = useMemo(() => {
    const blocks = customBlocks || [];
    const blockMap = new Map(blocks.map(b => [`custom-${b.id}`, b]));
    return definition.nodes.map(n => {
      let data = n.data;
      if (data?.integration === 'custom' && blockMap.size > 0) {
        const cb = blockMap.get(data.action);
        if (cb) {
          const groupIcon = blocks.find(b => b.integration_name === cb.integration_name && b.icon_url)?.icon_url || '';
          const groupIconDark = blocks.find(b => b.integration_name === cb.integration_name && b.icon_url_dark)?.icon_url_dark || '';
          data = {
            ...data,
            custom_icon_url: data.custom_icon_url || cb.icon_url || groupIcon || '',
            custom_icon_url_dark: data.custom_icon_url_dark || cb.icon_url_dark || groupIconDark || '',
            custom_color: data.custom_color || cb.color || '',
            integration_name: data.integration_name || cb.integration_name || '',
          };
        }
      }
      if (data?.source === 'custom_webhook') {
        let cb: typeof blocks[0] | undefined;
        if (data.custom_block_id) cb = blocks.find(b => b.id === data.custom_block_id);
        if (!cb && data.template_id) {
          const tid = (data.template_id as string).replace('custom-trigger-', '');
          cb = blocks.find(b => b.id === tid);
        }
        if (!cb) cb = blocks.find(b => b.block_type === 'trigger');
        if (cb) {
          const m = cb;
          const groupIcon = blocks.find(b => b.integration_name === m.integration_name && b.icon_url)?.icon_url || '';
          const groupIconDark = blocks.find(b => b.integration_name === m.integration_name && b.icon_url_dark)?.icon_url_dark || '';
          data = {
            ...data,
            custom_icon_url: data.custom_icon_url || m.icon_url || groupIcon || '',
            custom_icon_url_dark: data.custom_icon_url_dark || m.icon_url_dark || groupIconDark || '',
            custom_color: data.custom_color || m.color || '',
            integration_name: data.integration_name || m.integration_name || '',
          };
        }
      }
      return { id: n.id, type: n.type, data, position: n.position } as Node;
    });
  }, [definition.nodes, customBlocks]);

  const edges: Edge[] = useMemo(() =>
    definition.edges.map((e: any, i: number) => ({
      id: e.id || `pe-${i}`,
      source: e.source,
      target: e.target,
      label: e.label,
      type: 'workflow',
      markerEnd: MARKER_END,
      animated: true,
    } as Edge)),
    [definition.edges], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const containerHeight = useMemo(() => {
    const DEFAULT_HEIGHT = 220;
    const MAX_HEIGHT = 500;
    const NODE_HEIGHT = 80;
    const PADDING = 60;
    if (!definition.nodes.length) return DEFAULT_HEIGHT;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const n of definition.nodes) {
      const y = n.position?.y ?? 0;
      if (y < minY) minY = y;
      if (y + NODE_HEIGHT > maxY) maxY = y + NODE_HEIGHT;
    }
    const needed = maxY - minY + PADDING;
    return Math.min(Math.max(needed, DEFAULT_HEIGHT), MAX_HEIGHT);
  }, [definition.nodes]);

  return (
    <div className="rounded-lg border bg-muted/20 overflow-hidden" style={{ height: containerHeight }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={previewNodeTypes}
        edgeTypes={previewEdgeTypes}
        fitView
        fitViewOptions={{ padding: 0.25, maxZoom: 0.85 }}
        minZoom={isMobile ? 0.1 : 0.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: true }}
        style={{ background: 'transparent' }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
      </ReactFlow>
    </div>
  );
}

function formatDuration(start: Date, end: Date): string {
  const ms = end.getTime() - start.getTime();
  if (ms < 1000) return '<1s';
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainSecs = secs % 60;
  if (mins < 60) return `${mins}m ${remainSecs}s`;
  const hours = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `${hours}h ${remainMins}m`;
}

export default WorkflowDetail;
